
import { Star, Clock, Briefcase, MapPin, DollarSign, Globe, Award, Users, CheckCircle2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Service } from "@/types/marketplace";
import { IconRenderer } from "./IconRenderer";
import { memo } from "react";

interface ServiceCardProps {
  service: Service;
  onHire: (id: number) => void;
  onContact: (id: number) => void;
}

// Using memo to prevent unnecessary re-renders
export const ServiceCard = memo(({ service, onHire, onContact }: ServiceCardProps) => {
  return (
    <Card className="hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
      <CardHeader className="flex flex-row items-center gap-4">
        <IconRenderer iconName={service.iconName} className="w-6 h-6" />
        <div className="flex-1">
          <CardTitle className="text-xl font-semibold text-gray-900">
            {service.title}
          </CardTitle>
          <div className="text-lg text-sky-600 font-medium flex items-center gap-1">
            <DollarSign className="w-4 h-4" />
            {service.price}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2 mb-4">
          <span className="font-medium flex items-center gap-1">
            {service.seller}
            {service.verified && (
              <Tooltip>
                <TooltipTrigger>
                  <CheckCircle2 className="w-4 h-4 text-sky-500" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Verified Provider</p>
                </TooltipContent>
              </Tooltip>
            )}
          </span>
          <div className="flex items-center">
            <Star className="w-4 h-4 text-yellow-400 fill-current" />
            <span className="ml-1 text-sm">{service.rating}</span>
          </div>
          <span className="text-sm text-gray-500">({service.reviews} reviews)</span>
        </div>

        <p className="text-gray-600 mb-4">{service.description}</p>
        
        <div className="flex flex-wrap gap-2 mb-4">
          {service.skills.slice(0, 3).map((skill) => (
            <Badge
              key={skill}
              variant="secondary"
              className="bg-sky-50 text-sky-700 hover:bg-sky-100"
            >
              {skill}
            </Badge>
          ))}
          {service.skills.length > 3 && (
            <Badge variant="outline">+{service.skills.length - 3} more</Badge>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
          <div className="flex items-center gap-2 text-gray-600">
            <Clock className="w-4 h-4" />
            <span>{service.responseTime}</span>
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <Briefcase className="w-4 h-4" />
            <span>{service.completedProjects} projects</span>
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <MapPin className="w-4 h-4" />
            <span>{service.location}</span>
          </div>
          {service.teamSize && (
            <div className="flex items-center gap-2 text-gray-600">
              <Users className="w-4 h-4" />
              <span>Team of {service.teamSize}</span>
            </div>
          )}
        </div>

        {service.languages && (
          <div className="flex items-center gap-2 mb-4 text-sm text-gray-600">
            <Globe className="w-4 h-4" />
            <div className="flex gap-2">
              {service.languages.slice(0, 2).map((lang) => (
                <Badge key={lang} variant="outline">
                  {lang}
                </Badge>
              ))}
              {service.languages.length > 2 && (
                <Badge variant="outline">+{service.languages.length - 2}</Badge>
              )}
            </div>
          </div>
        )}

        {service.expertise && (
          <div className="flex items-center gap-2 mb-4 text-sm text-gray-600">
            <Award className="w-4 h-4" />
            <span>{service.expertise}</span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Button 
            className="w-full bg-emerald-600 hover:bg-emerald-700"
            onClick={() => onHire(service.id)}
          >
            Hire Now
          </Button>
          <Button 
            variant="outline"
            className="w-full border-sky-600 text-sky-600 hover:bg-sky-50"
            onClick={() => onContact(service.id)}
          >
            Contact
          </Button>
        </div>
      </CardContent>
    </Card>
  );
});

ServiceCard.displayName = "ServiceCard";

